import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import type { PartialId } from '@attain-apps/string-builder'

import * as state from '../../../../../../../../../../../state'
import * as display from '../../../../../../../../display'
import { Store } from '../../../store'

const NON_EDITING_ATTRIBUTES = new Set<PartialId>([
  ents.core.dms.data.Record.Id.KEY,
  '!search_string',
  '!resume_file',
  '!code',
  '!person',
  '!active_periods',
])

/**
 * Body of modal used to edit org chart nodes
 */
@ngCore.Component({
  imports: [
    ds.input.text.Component,
    ds.input.textArea.Component,
    ds.input.select.single.Component,
    ds.input.file.Component,
    ds.button.Component,
    display.value.Component,
  ],
  selector: 'atn-summit-org-chart-node-panel-general-tab',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link Store} */
  private readonly _store = ngCore.inject(Store)
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.fileMgmt.Feature} */
  private readonly _fileMgmtFeature = ngCore.inject(state.fileMgmt.Feature)

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** Indicates whether a resume file is currently being uploaded */
  public readonly isUploadingFileSg = ngCore.computed(
    (treeEditorFeature = this._store.treeEditorFeature) =>
      treeEditorFeature.selectors.isUploadingResumeFile.asSignal(),
  )

  /** FIXME:  */
  public readonly editingNodeSg = ngCore.computed(
    (chartId = this._store.chartIdSg()) =>
      chartId &&
      this._store.treeEditorFeature.selectors
        .getEditingNode({ chartId })
        .asSignal(),
  )
  /** Whether the currently edited node belongs to the Current chart */
  public readonly isCurrentChartSg = ngCore.computed(
    (chartId = this._store.chartIdSg()) =>
      chartId &&
      state.mosaic.treeEditor.models.Chart.Entry.get(chartId) === 'current',
  )
  /** Currently selected files */
  public readonly selectedFileSg = ngCore.signal<File | undefined>(undefined)

  /** Reference to the resume file associated with the editing node */
  public readonly resumeFileSg = ngCore.computed(
    (editingNode = this.editingNodeSg()) =>
      editingNode?.values?.['!resume_file'] as string | undefined,
  )

  /** Effect listening for file changes */
  private readonly _onFileChange = ngCore.effect(() => {
    const file = this.selectedFileSg()
    if (file) {
      this.uploadButtonClickHandler()
    }
  })

  /** TODO : */
  private readonly _nodesModelSg = ngCore.computed(
    (
      editingNode = this.editingNodeSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => editingNode?.modelId && schema?.tryGetModel(editingNode.modelId),
  )
  /** TODO : */
  private readonly _nodeAttributesSg = ngCore.computed(
    (modelOfEditingNodeSg = this._nodesModelSg()) =>
      modelOfEditingNodeSg?.attributes ?? [],
  )
  /** TODO:  */
  public readonly nodeAttributesSg = ngCore.computed(
    (nodeAttributesSg = this._nodeAttributesSg()) =>
      nodeAttributesSg.filter(
        a =>
          !NON_EDITING_ATTRIBUTES.has(
            ents.core.dms.models.Id.Parts.from(a.id).partialId,
          ),
      ),
  )

  /** TODO:  */
  public readonly attributeIdToPartialIdMap = ngCore.computed(
    (nodeAttributes = this.nodeAttributesSg()) =>
      nodeAttributes.reduce<
        Partial<Record<ents.core.dms.models.Attribute.Id, PartialId>>
      >((map, attr) => {
        map[attr.id] = ents.core.dms.models.Id.Parts.from(attr.id).partialId

        return map
      }, {}),
  )
  public onValueChange(
    id: ents.core.dms.models.Attribute.Id,
    value: ents.core.dms.data.Value,
  ) {
    const chartId = this._store.chartIdSg()
    const partialId = ents.core.dms.models.Id.Parts.from(id).partialId

    if (chartId) {
      this._store.treeEditorFeature.actions.editNodeValue
        .from(`OrgChart.EditNodeModal.Body.Attribute-${partialId}.Changed`)
        .create({
          attributeId: id,
          chartId,
          value,
        })
        .dispatch()
    }
  }

  /** Handler to upload file */
  public uploadButtonClickHandler(): void {
    const file = this.selectedFileSg()
    const chartId = this._store.chartIdSg()
    if (!file || !chartId) {
      return
    }

    this._store.treeEditorFeature.actions.uploadFile
      .from('TreeEditor.NodePanel.UploadButton.Clicked')
      .dispatch({ chartId, file })
  }

  /** Handler to download file */
  public downloadFileButtonClickHandler(): void {
    const zoneContext =
      this._meFeature.contexts.selectors.zoneContext.asSignal()
    const editingNode = this.editingNodeSg()

    const employeeId = editingNode?.values?.[ents.core.dms.data.Record.Id.KEY]
    const employeeResumeFile = editingNode?.values?.['!resume_file'] as
      | string
      | undefined
    if (!zoneContext || !editingNode || !employeeId || !employeeResumeFile) {
      return
    }

    const { name, extension } =
      state.fileMgmt.models.FileRequest.getFileInfo(employeeResumeFile)

    this._fileMgmtFeature.actions.download
      .from('TreeEditor.NodePanel.DownloadButton.Clicked')
      .dispatch({
        context: zoneContext,
        extension: extension,
        id: name,
        purpose: ents.core.common.fileManagement.Purpose.FileManagement,
        qualifier: employeeId,
      })
  }

  /**
   * Handle file selection
   */
  public onFileChange(file: File | undefined): void {
    if (file) {
      this.selectedFileSg.set(file)
    }
  }
}
